// Every call is gated server-side on `pharmacy:transfer` and branch-scoped
// (routes/pharmacy-transfers.js).
import { api } from '@/lib/api/client';
import { authFetch } from '@/api';
import type {
  IssueTransferInput, ReceiveTransferInput, StockTransferDetail, TransferFilter, TransferList, TransferStockBatch, TransferStore,
  Indent, IndentDetail, IndentFilter, LevelChange, NewIndentInput, StockLevelRow, SuggestedIndentLine,
} from './types';

// ── Indents (routes/pharmacy-indents.js, pharmacy:transfer) ──────
export function fetchIndents(status: IndentFilter, storeId?: number): Promise<Indent[]> {
  return api.get('/pharmacy-indents', { query: { status, store_id: storeId } });
}

export function fetchIndent(id: number): Promise<IndentDetail> {
  return api.get(`/pharmacy-indents/${id}`);
}

export function fetchIndentSuggestion(storeId: number): Promise<SuggestedIndentLine[]> {
  return api.get('/pharmacy-indents/suggest', { query: { store_id: storeId } });
}

export function createIndent(input: NewIndentInput): Promise<IndentDetail> {
  return api.post('/pharmacy-indents', input);
}

export function cancelIndent(id: number, reason: string): Promise<IndentDetail> {
  return api.post(`/pharmacy-indents/${id}/cancel`, { reason });
}

export function closeIndent(id: number, reason: string): Promise<IndentDetail> {
  return api.post(`/pharmacy-indents/${id}/close`, { reason });
}

// ── Reorder levels (routes/pharmacy-stock-levels.js) ─────────────
export function fetchStockLevels(storeId: number, belowOnly = false): Promise<StockLevelRow[]> {
  return api.get('/pharmacy-stock-levels', { query: { store_id: storeId, below: belowOnly ? 1 : undefined } });
}

/** Needs admin:pharmacy-stores. */
export function saveStockLevels(storeId: number, levels: LevelChange[]): Promise<{ changes: unknown[] }> {
  return api.put(`/pharmacy-stock-levels/${storeId}`, { levels });
}

export function fetchTransfers(status: TransferFilter, storeId?: number): Promise<TransferList> {
  return api.get('/pharmacy-transfers', { query: { status, store_id: storeId } });
}

export function fetchTransfer(id: number): Promise<StockTransferDetail> {
  return api.get(`/pharmacy-transfers/${id}`);
}

export function fetchTransferStores(): Promise<TransferStore[]> {
  return api.get('/pharmacy-transfers/stores');
}

/** medicineIds: every batch of these medicines (filling an indent); else a text search capped at 50. */
export function fetchTransferStock(storeId: number, q: string, medicineIds?: number[]): Promise<TransferStockBatch[]> {
  return api.get('/pharmacy-transfers/stock', {
    query: { store_id: storeId, q, medicine_ids: medicineIds?.length ? medicineIds.join(',') : undefined },
  });
}

export function issueTransfer(input: IssueTransferInput): Promise<StockTransferDetail> {
  return api.post('/pharmacy-transfers', input);
}

export function receiveTransfer(id: number, input: ReceiveTransferInput): Promise<StockTransferDetail> {
  return api.post(`/pharmacy-transfers/${id}/receive`, input);
}

export function cancelTransfer(id: number, reason: string): Promise<StockTransferDetail> {
  return api.post(`/pharmacy-transfers/${id}/cancel`, { reason });
}

/**
 * Prints the transfer slip. Fetched with the auth headers (a plain link can't
 * carry them) and printed from an off-screen iframe on this page — no popup,
 * so no popup blocker can swallow it. print() is called from here: the slip
 * carries no script, since the CSP blocks inline handlers (and a srcdoc frame
 * inherits it).
 */
export async function openTransferSlip(id: number): Promise<void> {
  const res = await authFetch(`/api/pharmacy-transfers/${id}/slip`);
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Could not open the slip (HTTP ${res.status})`);
  }
  const html = await res.text();
  document.getElementById('hms-transfer-slip-frame')?.remove();
  const frame = document.createElement('iframe');
  frame.id = 'hms-transfer-slip-frame';
  frame.title = 'Transfer slip';
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('style', 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;');
  frame.onload = () => { frame.contentWindow?.focus(); frame.contentWindow?.print(); };
  frame.srcdoc = html;
  document.body.appendChild(frame);
  // Left in place until the next slip — removing it during the print dialog cancels the print in some browsers.
}
